// What the salon did with Bailey's plans (approved, part-approved, or not now
// and why). Kept on the server per salon (per stylist for the self-employed:
// GET/PUT marketing/plans/memory), with this browser's copy as a cache so the
// planner reads it synchronously and it still works offline.
//
// Shape: { dismissed: { key: { at, reason } },
//          approved:  { key: { at, created: [{ channel, id }] } },
//          progress:  { key: { at, done: { itemId: { channel, id } } } } }
// progress is a plan where some items went and some didn't, so after a reload
// it's offered to finish (the done items stay done), never afresh.
//
// Writes are patches, merged key by key on the server (null removes a key), so
// two devices can't undo each other. A patch that can't reach the server is
// queued here and sent on the next hydrate.
import { marketingBackend } from '../../../services/marketingBackend'

const salon = () => localStorage.getItem('salonId') || 'me'
const KEY = () => `beu-bailey-plans-${salon()}`
const PENDING = () => `beu-bailey-plans-pending-${salon()}`
const MIGRATED = () => `beu-bailey-plans-migrated-${salon()}`
const EVENT = 'beu-bailey-plans'
const MAPS = ['dismissed', 'approved', 'progress']

const read = (k) => {
  try { return JSON.parse(localStorage.getItem(k) || 'null') } catch (e) { return null }
}
const write = (k, v) => {
  try { if (v == null) localStorage.removeItem(k); else localStorage.setItem(k, JSON.stringify(v)) } catch (e) { /* private mode */ }
}
const emit = () => { try { window.dispatchEvent(new CustomEvent(EVENT)) } catch (e) { /* old browsers */ } }
const shape = (m) => ({ dismissed: (m && m.dismissed) || {}, approved: (m && m.approved) || {}, progress: (m && m.progress) || {} })
const isEmpty = (m) => MAPS.every(k => !Object.keys((m && m[k]) || {}).length)

// Apply a patch ({ map: { key: entry | null } }) to a memory
const applyPatch = (m, patch) => {
  const out = shape(m)
  MAPS.forEach((map) => {
    if (!patch[map]) return
    const next = { ...out[map] }
    Object.keys(patch[map]).forEach((k) => {
      if (patch[map][k] === null) delete next[k]
      else next[k] = patch[map][k]
    })
    out[map] = next
  })
  return out
}
// Two patches as one (later wins, a null stays a null)
const mergePatch = (a, b) => {
  const out = {}
  MAPS.forEach((map) => {
    if ((a && a[map]) || (b && b[map])) out[map] = { ...((a && a[map]) || {}), ...((b && b[map]) || {}) }
  })
  return out
}

export const loadMemory = () => shape(read(KEY()))

// The server's copy, with anything still queued here kept on top
const fromServer = (res) => {
  const pending = read(PENDING())
  const m = pending ? applyPatch(shape(res), pending) : shape(res)
  write(KEY(), m)
  emit()
  return m
}

// Send anything queued; true when nothing is left waiting
const flush = async () => {
  const pending = read(PENDING())
  if (!pending) return true
  try {
    const res = await marketingBackend.planMemoryPatch(pending)
    // A write made while this was in flight is still queued; don't drop it.
    if (JSON.stringify(read(PENDING())) === JSON.stringify(pending)) write(PENDING(), null)
    fromServer(res)
    return true
  } catch (e) {
    return false
  }
}

const patch = (p) => {
  const m = applyPatch(loadMemory(), p)
  write(KEY(), m)
  emit()
  write(PENDING(), mergePatch(read(PENDING()), p))
  flush()
  return m
}

// Load from the server. The first time on this salon, anything this browser
// kept before the server did is sent up (merged, never replacing).
export const hydrateMemory = async () => {
  if (!read(MIGRATED())) {
    const local = loadMemory()
    if (!isEmpty(local)) write(PENDING(), mergePatch(local, read(PENDING())))
  }
  const flushed = await flush()
  if (flushed) write(MIGRATED(), true)
  try {
    const res = await marketingBackend.planMemoryGet()
    write(MIGRATED(), true)
    return fromServer(res)
  } catch (e) {
    return loadMemory()
  }
}

const now = () => new Date().toISOString()
export const dismissPlan = (key, reason = 'later') => patch({ dismissed: { [key]: { at: now(), reason } } })
export const undismissPlan = (key) => patch({ dismissed: { [key]: null } })
// created: [{ channel, id }] what approving made (campaign ids, post ids)
export const approvePlanMemory = (key, created = []) => patch({ approved: { [key]: { at: now(), created } }, progress: { [key]: null } })
// done: { itemId: { channel, id } } the items of a plan that have gone so far
export const saveProgress = (key, done = {}) => patch({ progress: { [key]: { at: now(), done } } })
export const progressFor = (key) => (loadMemory().progress[key] || null)

export const subscribeMemory = (fn) => {
  const on = () => fn()
  window.addEventListener(EVENT, on)
  return () => window.removeEventListener(EVENT, on)
}
